import React from 'react';
import { Box, Paper, Typography, Divider, TextField, Button, Alert } from '@mui/material';
import Header from '../widgets/Header';
import Loading from '../widgets/Loading';
import CurrencySelector from '../widgets/CurrencySelector';
import { changePassword, updateCurrency } from '../../services/auth';
import { useTranslation } from 'react-i18next';

function MyAccount({ user, setUser }) {

    const { t } = useTranslation();

    const [oldPassword, setOldPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [currency, setCurrency] = React.useState(user.currency || "USD");

    const handleCurrencyChange = async (newCurrency) => {
        setIsLoading(true);
        setSuccess('');
        setError('');
        console.log("Currency changed to: ", newCurrency);
        if (!newCurrency) {
            setError(t('my_account.change_currency_generic_error'));
            setIsLoading(false);
            return;
        }
        
        try {
            await updateCurrency(user._id, newCurrency);
            setCurrency(newCurrency);
            setUser((prevUser) => ({
                ...prevUser,
                currency: newCurrency,
            }));
        }
        catch (error) {
            if (error.response) {
                setError(error.response.data.message);
            } else {
                setError(t('my_account.change_currency_generic_error'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChange = async () => {
        setIsLoading(true);
        setSuccess('');
        setError('');
        
        if (!oldPassword || !newPassword || !confirmPassword) {
            setError(t('my_account.change_password_all_fields_are_mandatory_error'));
            setIsLoading(false);
            return;
        }
        if (newPassword.length < 8) {
            setError(t('my_account.change_password_minumum_length_error'));
            setIsLoading(false);
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('my_account.change_password_new_password_error'));
            setIsLoading(false);
            return;
        }

        if (newPassword === oldPassword) {
            setError(t('my_account.change_password_should_not_be_same_error'));
            setIsLoading(false);
            return;
        }
        setError('');
        
        // Add logic to handle password change
        await changePassword(user.email, oldPassword, newPassword)
            .then((response) => {
                console.log(response)
                if (response.accessToken) {
                    setSuccess(t('my_account.change_password_success'));
                }
            })
            .catch((error) => {
                if (error.response) {
                    setError(error.response.data.message);
                } else {
                    setError(t('my_account.change_password_generic_error'));
                }
            });
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setIsLoading(false);
    };

    const validateNewPassword = (newPasswordConfirmation) => {
        if (newPassword.length < 8) {
            setError(t('my_account.change_password_minumum_length_error'));
        } else if (newPassword !== newPasswordConfirmation) {
            setError(t('my_account.change_password_new_password_error'));
        } else {
            setError('');
        }
        setConfirmPassword(newPasswordConfirmation);
    };

    return (
        <Box>
            <Header text={t('my_account.title')} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2 }}>
                <Paper sx={{ 
                    padding: 2, 
                    margin: 2, 
                    width: { xs: '100%', sm: '80%', md: '40%' },
                }}>
                    <Box sx={{ padding: 2 }}>
                        <Typography variant="h5" gutterBottom>{t('my_account.details')}</Typography>
                        <Typography variant="body2">{t('my_account.description')}</Typography>
                        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
                        <Typography variant="body1"><strong>ID:</strong> {user._id}</Typography>
                        <Typography variant="body1"><strong>{t('my_account.name')}:</strong> {user.name}</Typography>
                        <Typography variant="body1"><strong>{t('my_account.email')}:</strong> {user.email}</Typography>
                        <Box sx={{ marginTop: 2 }}>
                            <CurrencySelector 
                                selectedCurrency={currency}
                                onCurrencyChange={(newCurrency) => {
                                    handleCurrencyChange(newCurrency);
                                }
                            }/>
                        </Box>
                        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
                        <Typography variant="h5" gutterBottom>{t('my_account.change_password')}</Typography>
                        {error && (
                            <Alert
                                onClick={() => setError('')}
                                variant="filled"
                                severity="warning"
                                sx={{
                                position: 'fixed',
                                bottom: 16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: {
                                    xs: '90%',   // on extra-small screens
                                    sm: '80%',   // small screens
                                    md: '50%',   // medium and up
                                },
                                zIndex: 1300,
                                boxShadow: 3,
                                }}
                            >
                                {error}
                            </Alert>
                        )}
                        {success && (
                            <Alert severity="success" sx={{ marginBottom: 2 }}>
                                {success}
                            </Alert>
                        )}
                        <TextField
                            label={t('my_account.current_password')}
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                        />
                        <TextField
                            label={t('my_account.new_password')}
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                            }}
                        />
                        <TextField
                            label={t('my_account.confirm_new_password')}
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={confirmPassword}
                            onChange={(e) => {
                                validateNewPassword(e.target.value);
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handlePasswordChange}
                                disabled={isLoading}
                            >
                                {t('my_account.save_changes')}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
            <Loading 
                text={t('my_account.loading')}
                isLoading={isLoading} 
            /> 
        </Box>
    );
}

export default MyAccount;
